import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography } from '@material-ui/core';
import { MODES } from '../../../../utils/constant';
import { convertUnit, airRoundOff, getAirUnit, roundSetPointRange } from '../../../../utils/utility';
import { useMobileStyles } from '../styles';
import Icon from '../../../../utils/icons';
import I18n from '../../../../localization';
import { correctSetpointsForMode, normalizeSetpointValue } from '../util';

// Import existing components
import AirModeSelector from './AirModeSelector';
import FanModeSelector from './FanModeSelector';
import TemperatureControl from './TemperatureControl';
import DeviceNameInput from './DeviceNameInput';

export default function EditScenePopup({ 
  sceneData, 
  isDarkMode, 
  closePopup,
  availableModes,
  IQAirRange,
  IQAirTemperatureRange,
  presets = []
}) {
  const classes = useMobileStyles();
  const isF = getAirUnit() === 'F';
  const isNameEditable = Boolean(sceneData?.['editable-name']);
  const hasHeatSupport = Array.isArray(availableModes) && availableModes.includes(MODES.HEAT);
  const hasCoolSupport = Array.isArray(availableModes) && availableModes.includes(MODES.COOL);

  const mockIQAirRange = useMemo(() => {
    return IQAirRange 
  }, [IQAirRange]);

  const defaultSetpoints = useMemo(() => {
    const getConvertedBound = (value, boundType) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return null;

      const convertedValue = roundSetPointRange(convertUnit(numericValue));
      if (!isF && boundType === 'min') {
        return Math.ceil(convertedValue);
      }

      return convertedValue;
    };

    const heatMin = getConvertedBound(IQAirTemperatureRange?.['heat-setpoint-range']?.min, 'min');
    const coolMax = getConvertedBound(IQAirTemperatureRange?.['cool-setpoint-range']?.max, 'max');

    return {
      heat: Number.isFinite(heatMin) ? heatMin + 5 : 30,
      cool: Number.isFinite(coolMax) ? coolMax - 5 : 35,
    };
  }, [IQAirTemperatureRange, isF]);

  // Initialize state with scene data or defaults
  const [deviceMode, setDeviceMode] = useState(sceneData?.['temperature-mode'] || MODES.AUTO);
  const [fanMode, setFanMode] = useState(sceneData?.['fan-mode'] || MODES.AUTO);
  const [heatSetpoint, setHeatSetpoint] = useState(() => {
    const sceneHeatSetpoint = Number(sceneData?.['heat-setpoint']);
    const initialHeat = Number.isFinite(sceneHeatSetpoint)
      ? airRoundOff(convertUnit(sceneHeatSetpoint))
      : defaultSetpoints.heat;
    return normalizeSetpointValue(initialHeat, mockIQAirRange);
  });
  const [presetName, setPresetName] = useState(sceneData?.name || '');
  const [presetNameError, setPresetNameError] = useState({ status: false, message: '' });

  const isDuplicatePresetName = (value) => {
    const normalizedCandidate = (value || '').trim().toLowerCase();
    if (!normalizedCandidate) return false;

    const currentPresetId = sceneData?.['preset-id'];

    return (presets || []).some((preset) => {
      if (!preset) return false;
      if (currentPresetId && preset['preset-id'] === currentPresetId) return false;

      const normalizedPresetName = (preset.name || '').trim().toLowerCase();
      return normalizedPresetName === normalizedCandidate;
    });
  };

  const validatePresetName = (value) => {
    const trimmedValue = (value || '').trim();

    if (trimmedValue.length < 3) {
      return I18n.t('mobile.iqAirSetting.editScene.nameMinLength');
    }
    if (trimmedValue.length > 8) {
      return I18n.t('mobile.iqAirSetting.editScene.nameMaxLength');
    }

    const regex = /^[a-zA-Z0-9 ]+$/;
    if (!regex.test(trimmedValue)) {
      return I18n.t('mobile.iqAirSetting.editScene.nameInvalidChars');
    }

    if (isDuplicatePresetName(trimmedValue)) {
      return I18n.t('mobile.iqAirSetting.editScene.nameDuplicate');
    }

    return true;
  };

  const handlePresetNameChange = (e) => {
    const value = e.target.value;
    setPresetName(value);

    const status = validatePresetName(value);
    if (status !== true) {
      setPresetNameError({ status: true, message: status });
      return;
    }

    setPresetNameError({ status: false, message: '' });
  };
  const [coolSetpoint, setCoolSetpoint] = useState(() => {
    const sceneCoolSetpoint = Number(sceneData?.['cool-setpoint']);
    const initialCool = Number.isFinite(sceneCoolSetpoint)
      ? airRoundOff(convertUnit(sceneCoolSetpoint))
      : defaultSetpoints.cool;
    return normalizeSetpointValue(initialCool, mockIQAirRange);
  });

  // Mock iqAirDetails for existing components
  const mockIqAirDetails = {
    airMode: deviceMode,
    fanMode: fanMode
  };

  // Mock IQAirPoints for TemperatureControl
  const mockIQAirPoints = {
    heat_setpoint: heatSetpoint,
    cool_setpoint: coolSetpoint
  };

  useEffect(() => {
    if (deviceMode !== MODES.AUTO) return;

    const corrected = correctSetpointsForMode({
      airMode: MODES.AUTO,
      heat: heatSetpoint,
      cool: coolSetpoint,
      min: mockIQAirRange.min,
      max: mockIQAirRange.max,
    });

    if (corrected.heat_setpoint !== heatSetpoint) {
      setHeatSetpoint(corrected.heat_setpoint);
    }
    if (corrected.cool_setpoint !== coolSetpoint) {
      setCoolSetpoint(corrected.cool_setpoint);
    }
  }, [deviceMode, heatSetpoint, coolSetpoint, mockIQAirRange]);

  const handleModeChange = (mode) => {
    setDeviceMode(mode);

    if (mode === MODES.AUTO) {
      const corrected = correctSetpointsForMode({
        airMode: MODES.AUTO,
        heat: heatSetpoint,
        cool: coolSetpoint,
        min: mockIQAirRange.min,
        max: mockIQAirRange.max,
      });
      setHeatSetpoint(corrected.heat_setpoint);
      setCoolSetpoint(corrected.cool_setpoint);
    }
  };

  const handleFanModeChange = (mode) => {
    setFanMode(mode);
  };

  const handleSliderChange = (val, config) => {
    const values = Array.isArray(val) ? val : [val];
    const bounds = { min: config.min, max: config.max };

    if (config.isAutoMode) {
      let [heat, cool] = values;
      const corrected = correctSetpointsForMode({
        airMode: MODES.AUTO,
        heat,
        cool,
        min: bounds.min,
        max: bounds.max,
      });
      setHeatSetpoint(corrected.heat_setpoint);
      setCoolSetpoint(corrected.cool_setpoint);
    }

    if (config.isHeatMode) {
      let [heat] = values;
      setHeatSetpoint(normalizeSetpointValue(heat, bounds));
    }

    if (config.isCoolMode) {
      let [cool] = values;
      setCoolSetpoint(normalizeSetpointValue(cool, bounds));
    }
  };

  const handleSliderAfterChange = () => {
    // Handle any post-slider-change logic if needed
  };

  const getPresetPayloadSetpoint = (setpoint) => {
    const normalizedLocalSetpoint = normalizeSetpointValue(setpoint, mockIQAirRange);
    const convertedSetpoint = convertUnit(normalizedLocalSetpoint, 'FTOC');

    if (getAirUnit() === 'F') {
      return Math.ceil(convertedSetpoint);
    }

    return convertedSetpoint;
  };

  const handleClose = () => {
    const shouldIncludeHeatSetpoint = hasHeatSupport && [MODES.HEAT, MODES.AUTO].includes(deviceMode);
    const shouldIncludeCoolSetpoint = hasCoolSupport && [MODES.COOL, MODES.AUTO].includes(deviceMode);

    const updatedSceneData = {
      ...sceneData,
      'temperature-mode': deviceMode,
      'fan-mode': fanMode,
      ...(shouldIncludeHeatSetpoint ? { 'heat-setpoint': getPresetPayloadSetpoint(heatSetpoint) } : {}),
      ...(shouldIncludeCoolSetpoint ? { 'cool-setpoint': getPresetPayloadSetpoint(coolSetpoint) } : {})
    };

    if (isNameEditable) {
      const validationResult = validatePresetName(presetName);
      if (validationResult !== true) {
        setPresetNameError({ status: true, message: validationResult });
        return;
      }
      updatedSceneData.name = presetName.trim();
    }
    
    if (closePopup) {
      closePopup(updatedSceneData);
    }
  };

  
  return (
    <Box className={classes.editScenePopup}>
      {/* Header */}
      <Box className={classes.popupHeader}>
        <Typography className={classes.popupTitle}>
          {I18n.t('mobile.iqAirSetting.editScene.title', { sceneName: sceneData?.name || 'Scene' })}
        </Typography>
        
        <button onClick={handleClose} className={classes.closeButton}><Icon 
          src="close" 
          className={classes.closeIcon}
        /></button>
      </Box>
      <Box className={classes.popupContent}>

        {isNameEditable && (
          <Box className={classes.popupSection}>
            <DeviceNameInput
              isEditScene
              iqAirDetails={{ name: presetName }}
              onNameChange={handlePresetNameChange}
              IQAirNameError={presetNameError}
              label={I18n.t('mobile.iqAirSetting.iqAirName')}
              maxLength={8}
              inputId="iqAirPresetName"
              useCardWithLabel={false}
            />
          </Box>
        )}

        <Box className={classes.popupSection}>
          <AirModeSelector
            availableModes={availableModes}
            iqAirDetails={mockIqAirDetails}
            onModeChange={handleModeChange}
            isDarkMode={isDarkMode}
            iqAirId="edit-scene"
          />
        </Box>

        {/* Setpoints Section */}
        <Box className={[MODES.COOL, MODES.AUTO, MODES.HEAT].includes(mockIqAirDetails.airMode) && classes.popupSection}>
          <TemperatureControl
            iqAirDetails={mockIqAirDetails}
            availableModes={availableModes}
            IQAirPoints={mockIQAirPoints}
            IQAirRange={mockIQAirRange}
            IQAirTemperatureRange={IQAirTemperatureRange}
            isDarkMode={isDarkMode}
            onSliderChange={handleSliderChange}
            onSliderAfterChange={handleSliderAfterChange}
          />
        </Box>

        {/* Fan Settings Section */}
        <Box className={classes.popupSection}>
          <FanModeSelector
            name={I18n.t('mobile.iqAirSetting.editScene.fanSettings')}
            iqAirDetails={mockIqAirDetails}
            onFanModeChange={handleFanModeChange}
            isDarkMode={isDarkMode}
          />
        </Box>
      </Box>
    </Box>
  );
}
